import { useState } from "react";
import { BreathingOrb } from "@/academy/engines/living/BreathingOrb";
import type { KafBlock } from "@/lib/kaf-academy.functions";
import { Feather, Timer as TimerIcon, Eye, Wind, PenLine } from "lucide-react";

export function KafLessonBlocks({ blocks, hue = 44 }: { blocks: KafBlock[]; hue?: number }) {
  if (!blocks?.length) return null;
  return (
    <div className="flex flex-col gap-5">
      {blocks.map((b, i) => (
        <KafBlockRenderer key={i} block={b} hue={hue} />
      ))}
    </div>
  );
}

function KafBlockRenderer({ block, hue }: { block: KafBlock; hue: number }) {
  switch (block.kind) {
    case "prose":
      return <p className="text-foreground/85 leading-relaxed">{block.text}</p>;
    case "reflection":
      return (
        <Card icon={<Feather size={14} />} label="Reflection">
          <p className="text-foreground/85">{block.prompt}</p>
        </Card>
      );
    case "visualization":
      return (
        <Card icon={<Eye size={14} />} label="Visualization">
          <p className="text-foreground/85">{block.text}</p>
        </Card>
      );
    case "observation":
      return (
        <Card icon={<Eye size={14} />} label="Observation">
          <p className="text-foreground/85">{block.prompt}</p>
        </Card>
      );
    case "breath":
      return (
        <Card icon={<Wind size={14} />} label={`Breath · ${block.pattern ?? "box"}`}>
          <BreathingOrb pattern={(block.pattern as "box" | "4-7-8" | "coherent") ?? "box"} cycles={block.cycles ?? 6} hue={hue} />
        </Card>
      );
    case "timer":
      return (
        <Card icon={<TimerIcon size={14} />} label="Practice Timer">
          <TimerBlock seconds={block.seconds ?? 300} label={block.label} hue={hue} />
        </Card>
      );
    case "writing_prompt":
      return <WritingPromptBlock prompt={block.prompt ?? ""} minutes={block.minutes ?? 5} />;
    default:
      return null;
  }
}

function Card({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border hairline bg-obsidian/60 p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
        {icon} {label}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function TimerBlock({ seconds, label, hue }: { seconds: number; label?: string; hue: number }) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const mins = Math.floor(remaining / 60);
  const secs = String(remaining % 60).padStart(2, "0");

  function start() {
    setRunning(true);
    const id = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { window.clearInterval(id); setRunning(false); return 0; }
        return r - 1;
      });
    }, 1000);
  }

  return (
    <div className="flex flex-col items-center gap-3">
      {label && <p className="text-center text-sm text-foreground/75">{label}</p>}
      <div
        className="font-serif text-5xl text-gold-luminous"
        style={{ textShadow: `0 0 30px hsl(${hue} 85% 55% / 0.55)` }}
      >
        {mins}:{secs}
      </div>
      <div className="flex gap-2">
        {!running ? (
          <button onClick={start} className="rounded-full bg-gold-gradient px-4 py-2 text-sm font-semibold text-obsidian">
            {remaining === seconds ? "Begin" : "Resume"}
          </button>
        ) : (
          <button onClick={() => setRunning(false)} className="rounded-full border hairline px-4 py-2 text-sm">
            Pause
          </button>
        )}
        <button onClick={() => setRemaining(seconds)} className="rounded-full border hairline px-4 py-2 text-sm">
          Reset
        </button>
      </div>
    </div>
  );
}

function WritingPromptBlock({ prompt, minutes }: { prompt: string; minutes: number }) {
  const [text, setText] = useState("");
  return (
    <Card icon={<PenLine size={14} />} label={`Writing · ${minutes} min`}>
      <p className="text-foreground/85">{prompt}</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write freely…"
        className="mt-3 min-h-[160px] w-full rounded-lg border hairline bg-obsidian/70 p-3 text-sm outline-none focus:border-gold/60"
      />
      <p className="mt-2 text-xs text-foreground/50">Local draft — copy into your journal to save.</p>
    </Card>
  );
}
